import { useEffect, useMemo, useState } from "react";
import apiClient from "../../../../services/api";
import { relTime } from "../../../../utils";
import { cn } from "../../../../lib/cn";

// Phase 1 — MentionsPage migrated to Tailwind utilities. The page-title
// classes (`pg-title`, `pg-sub`), the global action button `rc-btn`, and
// the `fade` entry animation still come from legacy common-ui.css / core.css;
// they'll be replaced in Phase 5 once every other page has migrated.

const PILL_TONE = {
  type:    "bg-[rgba(64,117,193,0.12)] text-[#4075C1]",
  unread:  "bg-[rgba(245,158,11,0.12)] text-[#d97706]",
  read:    "bg-[rgba(16,185,129,0.12)] text-[#059669]",
  cleared: "bg-[rgba(107,107,107,0.12)] text-[#6b7280]",
};

function Pill({ tone = "type", children }) {
  return (
    <span className={cn(
      "inline-flex items-center px-[9px] py-1 rounded-full",
      "text-[10px] font-bold uppercase tracking-[0.04em]",
      PILL_TONE[tone],
    )}>
      {children}
    </span>
  );
}

function Stat({ value, label }) {
  return (
    <div className="min-w-[92px] px-3.5 py-3 rounded-2xl border border-rc-border bg-rc-surface shadow-rc-sm">
      <strong className="block text-lg text-rc-text-primary">{value}</strong>
      <span className="block mt-1 text-[11px] uppercase tracking-[0.06em] text-rc-text-dim">{label}</span>
    </div>
  );
}

export default function MentionsPage() {
  const [mentions, setMentions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    async function loadMentions() {
      try {
        const data = await apiClient.getMentions({ limit: 200 });
        if (!active) return;
        setMentions(data.mentions || []);
      } catch {
        if (!active) return;
        setMentions([]);
      } finally {
        if (active) setLoading(false);
      }
    }

    loadMentions();
    return () => {
      active = false;
    };
  }, []);

  async function handleMarkRead(id) {
    try {
      await apiClient.markMentionRead(id);
      setMentions((current) => current.map((mention) => (
        mention.id === id
          ? { ...mention, read: true }
          : mention
      )));
    } catch {
      // Keep the local state unchanged on request failure.
    }
  }

  async function handleMarkAllRead() {
    try {
      await apiClient.markAllMentionsRead();
      setMentions((current) => current.map((mention) => ({ ...mention, read: true })));
    } catch {
      // Keep the local state unchanged on request failure.
    }
  }

  const stats = useMemo(() => ({
    total: mentions.length,
    unread: mentions.filter((item) => !item.read).length,
    cleared: 0,
  }), [mentions]);

  const emptyCard = (text) => (
    <div className="px-4 py-9 rounded-2xl border border-dashed border-rc-border bg-rc-surface text-center text-rc-text-dim">
      {text}
    </div>
  );

  return (
    <div className="fade flex flex-col gap-[18px] px-9 py-8">
      <div className="flex flex-wrap items-start justify-between gap-5">
        <div>
          <div className="pg-title">Mentions</div>
          <div className="pg-sub">All mentions addressed to you from the database-driven mentions log.</div>
        </div>
        {stats.unread > 0 && (
          <button className="rc-btn" onClick={handleMarkAllRead}>Mark all read</button>
        )}
        <div className="flex flex-wrap gap-2.5">
          <Stat value={stats.total} label="Total" />
          <Stat value={stats.unread} label="Unread" />
          <Stat value={stats.cleared} label="Archived" />
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {loading && emptyCard("Loading mentions…")}
        {!loading && mentions.length === 0 && emptyCard("No mentions yet.")}
        {!loading && mentions.map((mention) => (
          <div
            key={mention.id}
            onClick={() => { if (!mention.read) handleMarkRead(mention.id); }}
            className={cn(
              "px-[18px] py-4 rounded-2xl border border-rc-border bg-rc-surface shadow-rc-sm cursor-pointer",
              !mention.read && "border-l-4 border-l-rc-accent",
            )}
          >
            <div className="flex items-center justify-between gap-3 mb-2">
              <div className="text-sm font-bold text-rc-text-primary">
                {mention.mentionedBy?.name ? `${mention.mentionedBy.name} mentioned you` : "Mention"}
              </div>
              <div className="text-xs text-rc-text-dim">
                {mention.createdAt ? relTime(mention.createdAt) : ""}
              </div>
            </div>
            <div className="text-[13px] leading-[1.6] text-rc-text-secondary">{mention.text}</div>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <Pill tone="type">Mention</Pill>
              <Pill tone="read">{mention.sourceType}</Pill>
              {!mention.read && <Pill tone="unread">Unread</Pill>}
              {mention.read && <Pill tone="read">Read</Pill>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
